import { useState } from 'react';
import { StyledButton } from './StyledButton';
import GameService from '../../services/GameService';
import React from 'react'; // Added missing import for React
import { SOLAGOTCHI_ANIMATION_CONFIGS } from '../../util/Animations';

export const OnboardingModal = ({ children, isOpen, onClose }) => {
    if (!isOpen) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50">
        <div className="bg-white bg-opacity-90 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
          <div className="flex justify-end">
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
          {children}
        </div>
      </div>
    );
  };

// Color options for the player character
const COLOR_OPTIONS = [
  { name: 'Yellow', value: 'yellow', path: '/sprites/solagotchi/solagotchi_yellow.png' },
  { name: 'Black', value: 'black', path: '/sprites/solagotchi/solagotchi_black.png' },
  { name: 'Blue', value: 'blue', path: '/sprites/solagotchi/solagotchi_blue.png' },
  { name: 'Gray', value: 'gray', path: '/sprites/solagotchi/solagotchi_gray.png' },
  { name: 'Purple', value: 'purple', path: '/sprites/solagotchi/solagotchi_purple.png' },
  { name: 'Red', value: 'red', path: '/sprites/solagotchi/solagotchi_red.png' },
];

// Animation options for the preview dropdown
const ANIMATION_OPTIONS = [
  { name: 'Static', value: 'static', description: 'Still pose' },
  { name: 'Idle 1', value: 'idle1', description: 'Breathing' },
  { name: 'Idle 2', value: 'idle2', description: 'Ear wag' },
  { name: 'Happy 1', value: 'happy1', description: 'Blinking' },
  { name: 'Happy 2', value: 'happy2', description: 'Spinning' },
  { name: 'Happy 3', value: 'happy3', description: 'Jumping' },
  { name: 'Happy 4', value: 'happy4', description: 'Dancing' },
  { name: 'Happy 5', value: 'happy5', description: 'Celebrating' },
  { name: 'Cool', value: 'cool', description: 'Sunglasses pose' },
  { name: 'Angry 1', value: 'angry1', description: 'Grumpy' },
  { name: 'Angry 2', value: 'angry2', description: 'Frustrated' },
  { name: 'Sad', value: 'sad1', description: 'Sniffling' },
  { name: 'Tired 1', value: 'tired1', description: 'Dozing' },
  { name: 'Tired 2', value: 'tired2', description: 'Exhausted' },
  { name: 'Tired 3', value: 'tired3', description: 'Sleeping' },
];

// Color navigation component with arrows
const ColorSelector = ({ selectedColor, onColorSelect }) => {
  const currentIndex = COLOR_OPTIONS.findIndex(color => color.value === selectedColor.value);
  
  const goToPrevious = () => {
    const prevIndex = currentIndex === 0 ? COLOR_OPTIONS.length - 1 : currentIndex - 1;
    onColorSelect(COLOR_OPTIONS[prevIndex]);
  };
  
  const goToNext = () => {
    const nextIndex = currentIndex === COLOR_OPTIONS.length - 1 ? 0 : currentIndex + 1;
    onColorSelect(COLOR_OPTIONS[nextIndex]);
  };
  
  return { goToPrevious, goToNext };
};

// Preview component using canvas to show the animated sprite with navigation arrows
const CharacterPreview = ({ selectedColor, onColorSelect }) => {
  const [spriteLoaded, setSpriteLoaded] = useState(false);
  const [spriteImage, setSpriteImage] = useState(null);
  const [selectedAnimation, setSelectedAnimation] = useState(ANIMATION_OPTIONS[1]); // Default to idle1
  const [currentFrame, setCurrentFrame] = useState(0);
  const [lastFrameTime, setLastFrameTime] = useState(0);

  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setSpriteImage(img);
      setSpriteLoaded(true);
    };
    img.src = selectedColor.path;
  }, [selectedColor.path]);

  const canvasRef = React.useRef(null);
  const animationRef = React.useRef(null);

  // Animation loop
  React.useEffect(() => {
    if (!spriteLoaded || !spriteImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Build the correct animation key using color and animation name
    const animationKey = `${selectedColor.value}_${selectedAnimation.value}`;
    const animConfig = SOLAGOTCHI_ANIMATION_CONFIGS[animationKey];
    
    if (!animConfig) {
      console.warn(`Animation config not found for: ${animationKey}`);
      return;
    }

    const animate = (timestamp) => {
      // Disable image smoothing for crisp pixel art
      ctx.imageSmoothingEnabled = false;
      ctx.webkitImageSmoothingEnabled = false;
      ctx.mozImageSmoothingEnabled = false;
      ctx.msImageSmoothingEnabled = false;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Get the frame indices array and current frame index
      const frameIndices = animConfig.frames.frames;
      const frameIndex = frameIndices[currentFrame];
      
      // Calculate frame position in spritesheet
      const frameWidth = 32;
      const frameHeight = 32;
      const framesPerRow = 5;
      const frameX = (frameIndex % framesPerRow) * frameWidth;
      const frameY = Math.floor(frameIndex / framesPerRow) * frameHeight;
      
      // Draw current frame
      ctx.drawImage(
        spriteImage,
        frameX, frameY, frameWidth, frameHeight, // Source rectangle
        0, 0, canvas.width, canvas.height // Destination rectangle
      );
      
      // For static animation (single frame), don't animate
      if (selectedAnimation.value === 'static' || frameIndices.length === 1) {
        return; // Stop here, don't request next frame
      }
      
      // For multi-frame animations, continue animating
      const frameRate = animConfig.frameRate || 3;
      const frameDuration = 1000 / frameRate; // Convert to milliseconds
      
      if (timestamp - lastFrameTime >= frameDuration) {
        // Update frame counter
        setCurrentFrame(prev => (prev + 1) % frameIndices.length);
        setLastFrameTime(timestamp);
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [spriteLoaded, spriteImage, selectedAnimation, selectedColor, currentFrame, lastFrameTime]);

  // Reset frame when animation or color changes
  React.useEffect(() => {
    setCurrentFrame(0);
    setLastFrameTime(0);
  }, [selectedAnimation, selectedColor]);

  const colorNavigation = ColorSelector({ selectedColor, onColorSelect });

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-3 text-center">Choose Your Binagotchi's Color</h3>
      <div className="flex items-center justify-center gap-6">
        <button
          onClick={colorNavigation.goToPrevious}
          className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        
        <div className="flex flex-col items-center">
          <div className="bg-gray-100 rounded-lg p-6 border-2 border-dashed border-gray-300">
            {spriteLoaded ? (
              <canvas
                ref={canvasRef}
                width={96}
                height={96}
                className="pixelated"
                style={{ 
                  imageRendering: 'pixelated',
                  imageRendering: '-moz-crisp-edges',
                  imageRendering: '-webkit-crisp-edges',
                  imageRendering: 'crisp-edges'
                }}
              />
            ) : (
              <div className="w-24 h-24 bg-gray-300 rounded animate-pulse" />
            )}
          </div>
          <p className="text-center text-sm text-gray-600 mt-3">
            Your {selectedColor.name} Binagotchi
          </p>
          
          {/* Animation selection dropdown */}
          <div className="mt-4 w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview Animation
            </label>
            <select
              value={selectedAnimation.value}
              onChange={(e) => setSelectedAnimation(ANIMATION_OPTIONS.find(anim => anim.value === e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent text-sm"
            >
              {ANIMATION_OPTIONS.map((animation) => (
                <option key={animation.value} value={animation.value}>
                  {animation.name} - {animation.description}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <button
          onClick={colorNavigation.goToNext}
          className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>
    </div>
  );
};
  
  export const OnboardingForm = ({ walletAddress, onComplete }) => {
    const [username, setUsername] = useState('');
    const [petName, setPetName] = useState('');
    const [selectedColor, setSelectedColor] = useState(COLOR_OPTIONS[0]); // Default to yellow
    const [error, setError] = useState('');
    const [gameService] = useState(() => new GameService(process.env.REACT_APP_API_URL || ''));
  
    // Add keyboard navigation for color selection
    React.useEffect(() => {
      const handleKeyPress = (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const currentIndex = COLOR_OPTIONS.findIndex(color => color.value === selectedColor.value);
          const prevIndex = currentIndex === 0 ? COLOR_OPTIONS.length - 1 : currentIndex - 1;
          setSelectedColor(COLOR_OPTIONS[prevIndex]);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const currentIndex = COLOR_OPTIONS.findIndex(color => color.value === selectedColor.value);
          const nextIndex = currentIndex === COLOR_OPTIONS.length - 1 ? 0 : currentIndex + 1;
          setSelectedColor(COLOR_OPTIONS[nextIndex]);
        }
      };
  
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }, [selectedColor]);
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        // Set wallet address before making API call
        gameService.setWalletAddress(walletAddress);
        // Register user with selected color
        const userData = await gameService.registerUser(username, petName, selectedColor.path);
        onComplete(userData);
      } catch (err) {
        setError(err.message);
      }
    };
  
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <h2 className="text-xl font-bold text-center mb-4">Register Your Pet</h2>
        {error && <div className="text-red-500 text-sm font-medium">{error}</div>}
        
        <CharacterPreview selectedColor={selectedColor} onColorSelect={setSelectedColor} />
        
        <p className="text-center text-xs text-gray-500 mb-4">
          Use arrow keys ← → or click the arrows to change colors
        </p>
        
        <input
          type="text"
          placeholder="Username"
          className="px-4 py-2 rounded-lg border border-red-300 bg-white/50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
          value={username}
          maxLength="10"
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder="Pet Name"
          className="px-4 py-2 rounded-lg border border-red-300 bg-white/50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
          value={petName}
          maxLength="10"
          onChange={(e) => setPetName(e.target.value)}
          required
        />
        <StyledButton type="submit">Register</StyledButton>
      </form>
    );
  };